// RAPIFAST :: Verificación del piloto — PANEL REAL del administrador.
// Lista los documentos de conductor y negocios PENDIENTES (admin_get_pending_verifications),
// abre archivos con URL firmada de corta duración y aprueba/rechaza vía las RPCs de 0024.
// Sin datos ficticios. La autorización real la imponen is_admin() (RPC) y las políticas de
// Storage (0025); el rol en el frontend es solo para la navegación, no es la frontera de seguridad.
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Ban, Eye, Loader2, AlertTriangle, ShieldCheck, Building2, Users } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, Stat, EmptyState } from "@/components/kit";
import { Badge, SectionCard } from "@/components/verificationKit";
import { getVerificationDocumentUrl } from "@/lib/verificationStorage";
import {
  DRIVER_DOC_LABELS, VERIF_STATUS_META, adminGetPendingVerifications,
  adminReviewDriverDocument, adminReviewBusinessVerification, isExpired,
} from "@/lib/verificationApi";

const inputCls =
  "rounded-lg border border-[#333] bg-[#1F1F1F] px-2.5 py-1.5 text-xs text-gray-200 outline-none focus:border-[#FF6A00]";

function ReviewActions({ onApprove, onReject, busy, testIdBase }) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const confirmReject = () => {
    if (!reason.trim()) { toast.error("El motivo de rechazo es obligatorio"); return; }
    onReject(reason.trim());
    setRejecting(false); setReason("");
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={onApprove} disabled={busy} data-testid={`${testIdBase}-approve`}
          className="flex items-center gap-1 rounded-lg bg-emerald-500/15 px-2.5 py-1.5 text-xs font-bold text-emerald-400 hover:bg-emerald-500/25 disabled:opacity-40">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Verificar
        </button>
        <button onClick={() => setRejecting((v) => !v)} disabled={busy} data-testid={`${testIdBase}-reject`}
          className="flex items-center gap-1 rounded-lg bg-red-500/10 px-2.5 py-1.5 text-xs font-bold text-red-400 hover:bg-red-500/20 disabled:opacity-40">
          <Ban size={13} /> Rechazar
        </button>
      </div>
      {rejecting && (
        <div className="flex flex-wrap items-center gap-2">
          <input className={`${inputCls} grow`} placeholder="Motivo del rechazo (obligatorio)"
            value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`${testIdBase}-reject-reason`} />
          <button onClick={confirmReject} disabled={busy} data-testid={`${testIdBase}-reject-confirm`}
            className="rounded-lg bg-red-500/20 px-3 py-1.5 text-xs font-bold text-red-300 hover:bg-red-500/30 disabled:opacity-40">
            Confirmar rechazo
          </button>
        </div>
      )}
    </div>
  );
}

export default function AdminVerification() {
  const { role } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [driverDocs, setDriverDocs] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [names, setNames] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setLoadError("");
    try {
      const data = await adminGetPendingVerifications();
      const dd = data?.driver_documents || [];
      const bz = data?.businesses || [];
      setDriverDocs(dd); setBusinesses(bz);
      const ids = [...new Set([...dd.map((d) => d.driver_id), ...bz.map((b) => b.business_id)])].filter(Boolean);
      if (ids.length) {
        const { data: profs } = await supabase.from("profiles").select("id,full_name,business_name").in("id", ids);
        setNames(Object.fromEntries((profs || []).map((p) => [p.id, p.business_name || p.full_name || p.id.slice(0, 8)])));
      } else {
        setNames({});
      }
    } catch (err) {
      setLoadError(err?.message || "No se pudieron cargar las verificaciones pendientes.");
      setDriverDocs([]); setBusinesses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDoc = async (storagePath) => {
    if (!storagePath) { toast.error("Este documento no tiene archivo asociado"); return; }
    try {
      const url = await getVerificationDocumentUrl(storagePath);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err?.message || "No se pudo abrir el documento (enlace firmado)");
    }
  };

  const reviewDoc = async (documentId, status, notes) => {
    setBusyId(documentId);
    try {
      await adminReviewDriverDocument({ documentId, status, notes });
      toast.success(status === "VERIFIED" ? "Documento verificado" : "Documento rechazado");
      await load();
    } catch (err) {
      toast.error(err?.message || "No se pudo aplicar la revisión");
    } finally { setBusyId(null); }
  };

  const reviewBiz = async (businessId, status, notes) => {
    setBusyId(businessId);
    try {
      await adminReviewBusinessVerification({ businessId, status, notes });
      toast.success(status === "VERIFIED" ? "Negocio verificado" : "Negocio rechazado");
      await load();
    } catch (err) {
      toast.error(err?.message || "No se pudo aplicar la revisión");
    } finally { setBusyId(null); }
  };

  const grouped = useMemo(() => {
    const m = {};
    driverDocs.forEach((d) => { (m[d.driver_id] = m[d.driver_id] || []).push(d); });
    return m;
  }, [driverDocs]);

  if (loading) return <Loader label="Cargando verificaciones pendientes…" />;

  return (
    <div data-testid="admin-verification-review">
      <PageHeader
        title="Verificación del piloto"
        subtitle="Revisión real de documentos de conductores y negocios · El Cerrito, Valle del Cauca"
        testId="admin-verification-review-header"
        action={
          <button onClick={load} data-testid="admin-verification-refresh"
            className="rounded-lg border border-[#333] px-3 py-1.5 text-xs font-bold text-gray-300 hover:bg-[#1F1F1F]">
            Actualizar
          </button>
        }
      />

      <div className="mb-5 flex items-start gap-3 rounded-2xl border border-[#27272A] bg-[#141416] px-4 py-3 text-xs text-gray-400">
        <ShieldCheck size={16} className="mt-0.5 shrink-0 text-[#FF6A00]" />
        Los archivos se abren con enlaces firmados temporales (5 min). El acceso lo autoriza el servidor
        (políticas de Storage y <code className="text-gray-300">is_admin()</code>); no se exponen URLs públicas permanentes.
      </div>

      {role !== "ADMIN" && (
        <div className="mb-5 rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          Esta sección es solo para administradores.
        </div>
      )}

      {loadError ? (
        <div className="flex items-start gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300"
          data-testid="admin-verification-load-error">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-bold">No se pudieron cargar las verificaciones pendientes.</div>
            <div className="mt-0.5 text-red-300/80">{loadError}</div>
            <button onClick={load} data-testid="admin-verification-retry"
              className="mt-2 rounded-lg border border-red-500/40 px-3 py-1.5 text-xs font-bold text-red-200 hover:bg-red-500/10">
              Reintentar
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Stat label="Documentos pendientes" value={driverDocs.length} accent="#FBBF24" testId="kpi-pending-docs" />
            <Stat label="Conductores con pendientes" value={Object.keys(grouped).length} accent="#60A5FA" testId="kpi-pending-drivers" />
            <Stat label="Negocios pendientes" value={businesses.length} accent="#FF6A00" testId="kpi-pending-businesses" />
          </div>

          <SectionCard title="Documentos de conductores pendientes" testId="admin-driver-docs-section">
            {Object.keys(grouped).length === 0 ? (
              <EmptyState icon={Users} title="Sin documentos pendientes" subtitle="No hay documentos de conductores por revisar." testId="admin-no-pending-docs" />
            ) : (
              <div className="space-y-4">
                {Object.entries(grouped).map(([driverId, rows]) => (
                  <div key={driverId} className="rounded-xl border border-[#1e1e21] bg-[#101012] p-3.5" data-testid={`pending-driver-${driverId}`}>
                    <div className="mb-2 flex items-center gap-2">
                      <Users size={15} className="text-gray-400" />
                      <span className="font-heading font-bold text-white">{names[driverId] || driverId.slice(0, 8)}</span>
                      <span className="text-xs text-gray-500">{rows.length} documento(s) pendiente(s)</span>
                    </div>
                    <div className="space-y-2">
                      {rows.map((d) => (
                        <div key={d.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-[#1e1e21] bg-[#141416] px-3 py-2.5"
                          data-testid={`doc-row-${d.id}`}>
                          <div className="min-w-[160px] grow">
                            <div className="text-sm font-semibold text-white">{DRIVER_DOC_LABELS[d.doc_type] || d.doc_type}</div>
                            {d.doc_number && <div className="text-xs text-gray-500">N.º: {d.doc_number}</div>}
                            {d.expires_at && (
                              <div className={`text-xs ${isExpired(d.expires_at) ? "font-bold text-red-400" : "text-gray-500"}`}>
                                Vence: {d.expires_at}{isExpired(d.expires_at) && " · VENCIDO"}
                              </div>
                            )}
                          </div>
                          <Badge tone={VERIF_STATUS_META[d.status]} testId={`doc-status-${d.id}`}>{VERIF_STATUS_META[d.status].label}</Badge>
                          <button onClick={() => openDoc(d.storage_path)} data-testid={`document-view-signed-url-button-${d.id}`}
                            className="flex items-center gap-1 rounded-lg bg-[#FF6A00]/15 px-2.5 py-1.5 text-xs font-bold text-[#FF6A00] hover:bg-[#FF6A00]/25">
                            <Eye size={13} /> Ver documento
                          </button>
                          <ReviewActions
                            busy={busyId === d.id}
                            testIdBase={`doc-${d.id}`}
                            onApprove={() => reviewDoc(d.id, "VERIFIED", null)}
                            onReject={(reason) => reviewDoc(d.id, "REJECTED", reason)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          <div className="mt-5">
            <SectionCard title="Negocios aliados pendientes" testId="admin-businesses-section">
              {businesses.length === 0 ? (
                <EmptyState icon={Building2} title="Sin negocios pendientes" subtitle="No hay negocios por revisar." testId="admin-no-pending-biz" />
              ) : (
                <div className="space-y-2">
                  {businesses.map((b) => (
                    <div key={b.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-[#1e1e21] bg-[#101012] px-3 py-2.5"
                      data-testid={`pending-business-${b.business_id}`}>
                      <div className="min-w-[180px] grow">
                        <div className="text-sm font-semibold text-white">{b.legal_name || names[b.business_id] || b.business_id.slice(0, 8)}</div>
                        <div className="text-xs text-gray-500">
                          {b.nit ? `NIT ${b.nit} · ` : ""}{b.contact_name || "sin contacto"}{b.contact_phone ? ` · ${b.contact_phone}` : ""}
                        </div>
                        {b.agreement_accepted_version && (
                          <div className="text-xs text-gray-500">Acuerdo aceptado: {b.agreement_accepted_version}</div>
                        )}
                      </div>
                      <Badge tone={VERIF_STATUS_META[b.status]} testId={`biz-status-${b.business_id}`}>{VERIF_STATUS_META[b.status].label}</Badge>
                      <ReviewActions
                        busy={busyId === b.business_id}
                        testIdBase={`biz-${b.business_id}`}
                        onApprove={() => reviewBiz(b.business_id, "VERIFIED", null)}
                        onReject={(reason) => reviewBiz(b.business_id, "REJECTED", reason)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        </>
      )}
    </div>
  );
}
